import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { IDocumentsContext } from './documents.interfaces';
import { DOCUMENT_TYPE } from 'app/helpers/documentTypes';
import DocumentsModal from 'modules/customer/components/DocumentsModal/DocumentsModal';

export const DocumentsContext = createContext<IDocumentsContext>({
  onCloseModal: () => {
    throw new Error('Método não implementado');
  },
  onOpenModal: () => {
    throw new Error('Método não implementado');
  },
  onSave: () => {
    throw new Error('Método não implementado');
  },
  onCancel: () => {
    throw new Error('Método não implementado');
  },
});

export interface IDocumentsProviderProps {
  children?: React.ReactNode;
}

export const DocumentsProvider = ({ children }: IDocumentsProviderProps) => {
  const [fileName, setFileName] = useState<string>();
  const [documentTypes, setDocumentTypes] = useState<{
    [key: string]: string;
  }>();
  const [excludeTypes, setExcludeTypes] = useState<DOCUMENT_TYPE[]>([]);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const promiseRef = useRef<{
    resolve?: (payload: DOCUMENT_TYPE | false) => void;
  }>({
    resolve: undefined,
  });
  const onOpenModal = useCallback(
    (
      _fileName: string,
      _documentTypes: {
        [key: string]: string;
      },
      _excludeTypes: DOCUMENT_TYPE[],
    ): Promise<DOCUMENT_TYPE | false> => {
      return new Promise<DOCUMENT_TYPE | false>((resolve) => {
        setDocumentTypes(_documentTypes);
        setExcludeTypes(_excludeTypes);
        setFileName(_fileName);
        setIsOpenModal(true);
        promiseRef.current.resolve = resolve;
      });
    },
    [],
  );

  const onCloseModal = useCallback(() => {
    setIsOpenModal(false);
    setIsLoading(false);
  }, []);

  const onSave = useCallback(
    (_documentType: DOCUMENT_TYPE) => {
      if (promiseRef.current?.resolve) {
        setIsLoading(true);
        promiseRef.current.resolve(_documentType);
      }
    },
    [promiseRef],
  );

  const onCancel = useCallback(() => {
    if (promiseRef.current?.resolve) {
      promiseRef.current.resolve(false);
    }
    setIsOpenModal(false);
  }, []);

  return (
    <DocumentsContext.Provider
      value={{
        onOpenModal,
        onCloseModal,
        onSave,
        onCancel,
      }}
    >
      {children}
      <DocumentsModal
        isOpen={isOpenModal}
        name={fileName ?? ''}
        excludeTypes={excludeTypes}
        documentTypes={documentTypes}
        handleClose={onCancel}
        handleConfirm={onSave}
        handleCancel={onCancel}
        isLoading={isLoading}
      />
    </DocumentsContext.Provider>
  );
};

export const useDocumentsContext = () => {
  const context = useContext(DocumentsContext);

  if (context === undefined) {
    throw new Error(
      'useDocumentsContext must be used within a DocumentsProvider',
    );
  }
  return context;
};
